import { Iam as Iam072021, ListTeamRolesOutput, ListTeamsOutput, ListTeamUserRolesOutput, ListUsersOutput } from "@smash-sdk/iam/ejs/07-2021";
import { Iam as Iam102019, ListPoliciesOutput, ListTeamRolesInput } from "@smash-sdk/iam/ejs/10-2019";
import { getDecodedToken } from "../../../shared/helpers/helpers";
import { Team } from "../../apiKey/models/Team.interface";
import { User } from "../../apiKey/models/Users.interface";
import { roles } from "../../roles/accessManagement/config/roles";
import { Policy } from "../../roles/accessManagement/policies";
import { Role, TeamRole, UserRole } from "../../roles/accessManagement/roles";
import { IdHelper } from "smash-idhelper";

interface HydratedRole {
    name: string;
    description: string;
    policies: {
        name: string;
        description: string;
        statement: {
            action: string[];
            effect: "Allow" | "Deny";
            resource: string[];
        }[];
    }[];
}

interface IPolicy {
    name: string;
    description: string;
    statement: {
        action: string[];
        effect: "Allow" | "Deny";
        resource: string[];
    }[];
}

export function listTeamRemoteRoles({ teamId }: ListTeamRolesInput): Promise<HydratedRole[]> {
    return new Promise(async (resolve, reject) => {
        try {
            const iam = new Iam102019({});
            const { roles } = await iam.listTeamRoles({ teamId });
            const filteredRoles = roles.filter(role => !role.name.startsWith('Managed'));
            const hydratedRoles = await (await Promise.all(filteredRoles.map(role => iam.getRole({ roleId: role.name })))).map(response => response.role);
            resolve(hydratedRoles);
        } catch (error) {
            reject(error);
        }
    })
}

export function listRemoteRoles(): Promise<HydratedRole[]> {
    return new Promise(async (resolve, reject) => {
        try {
            const iam = new Iam102019({});
            const { roles } = await iam.listRoles();
            const filteredRoles = roles.filter(role => !role.name.startsWith('Managed'));
            const hydratedRoles = await (await Promise.all(filteredRoles.map(role => iam.getRole({ roleId: role.name })))).map(response => response.role);
            resolve(hydratedRoles);
        } catch (error) {
            reject(error);
        }
    })
}

export function listRemotePolicies(): Promise<ListPoliciesOutput['policies']> {
    return new Promise(async (resolve, reject) => {
        try {
            const iam = new Iam102019({});
            const { policies } = await iam.listPolicies();
            const filteredPolicies = policies.filter(policy => !policy.name.startsWith('Managed'));
            resolve(filteredPolicies);
        } catch (error) {
            reject(error);
        }
    })
}

export function listTeams(): Promise<ListTeamsOutput['teams']> {
    return new Promise(async (resolve, reject) => {
        try {
            const iam = new Iam102019({});
            const { teams } = await iam.listTeams();
            resolve(teams);
        } catch (error) {
            reject(error);
        }
    })
}

export function listUsers(): Promise<ListUsersOutput['users']> {
    return new Promise(async (resolve, reject) => {
        try {
            const iam = new Iam102019({});
            const { users } = await iam.listUsers();
            resolve(users);
        } catch (error) {
            reject(error);
        }
    })
}

interface UserWithRoles {
    id: string;
    username: string;
    lastName: string;
    firstName: string;
    provisioned: boolean;
    groupsCount: number;
    teamsCount: number;
    status: string;
    label?: string | undefined;
    roles: {
        name: string;
        description: string;
    }[]
}

export function listTeamUsersWithRoles({ teamId }: { teamId: string }): Promise<UserWithRoles[]> {
    return new Promise(async (resolve, reject) => {
        try {
            const iam072021 = new Iam072021({});
            const teamUsersResponse = await iam072021.listTeamUsers({ teamId });
            const teamUsersWithRoles: Array<UserWithRoles> = [];
            for (const user of teamUsersResponse.users) {
                const teamUserRolesResponse = await listTeamUserRolesWithDefaultResponse({ teamId, userId: user.id });
                teamUsersWithRoles.push({ ...user, roles: teamUserRolesResponse.roles });
            }
            resolve(teamUsersWithRoles);
        } catch (error) {
            reject(error);
        }
    });
}

export function listTeamRolesWithDefaultResponse({ teamId }: { teamId: string }): Promise<ListTeamRolesOutput> {
    return new Promise(async (resolve) => {
        try {
            const iam072021 = new Iam072021({});
            const teamRolesResponse = await iam072021.listTeamRoles({ teamId });
            resolve(teamRolesResponse);
        } catch (error) {
            resolve({ roles: [] });
        }
    });
}

export function listTeamUserRolesWithDefaultResponse({ teamId, userId }: { teamId: string, userId: string }): Promise<ListTeamUserRolesOutput> {
    return new Promise(async (resolve) => {
        try {
            const iam072021 = new Iam072021({});
            const teamRolesResponse = await iam072021.listTeamUserRoles({ teamId, userId });
            resolve(teamRolesResponse);
        } catch (error) {
            resolve({ roles: [] });
        }
    });
}

export function executeUpdatePermissions(tasks: any): Promise<void> {
    return new Promise(async (resolve, reject) => {
        try {
            const { policiesToCreate, policiesToUpdate, policiesToDelete, rolesToDelete, rolesToCreate, rolesToUpdate } = tasks;
            const iam102019 = new Iam102019({});
            const iam072021 = new Iam072021({});

            const idHelper = new IdHelper();

            const batchCreateOrUpdatePolicies = [
                ...policiesToCreate.map((policy: Policy) => iam102019.createPolicy(policy)),
                ...policiesToUpdate.map((policy: Policy) => iam102019.updatePolicy({ policyId: policy.name, statement: policy.statement, description: policy.description })),
            ];
            await Promise.all(batchCreateOrUpdatePolicies);
            for (const roleToCreate of rolesToCreate) {
                const { role } = await iam102019.createRole(roleToCreate);
                const id = role.name.split('$')[0];
                if (idHelper.getTypeFromId(id) === 'team') {
                    await iam102019.attachTeamRole({ teamId: id, roleId: role.name }).catch;
                }
                if (idHelper.getTypeFromId(id) === 'user') {
                    await iam102019.attachUserRole({ userId: id, roleId: role.name }).catch;
                }
            }

            for (const roleToUpdate of rolesToUpdate) {
                const reqs = [];
                if (roleToUpdate.policiesToAttach.length) {
                    reqs.push(...roleToUpdate.policiesToAttach.map((policy: Policy) => iam102019.attachRolePolicy({ roleId: roleToUpdate.role.name, policyId: policy.name })));
                }
                if (roleToUpdate.policiesToDetach.length) {
                    reqs.push(...roleToUpdate.policiesToDetach.map((policy: Policy) => iam102019.detachRolePolicy({ roleId: roleToUpdate.role.name, policyId: policy.name })));
                }
                await Promise.all(reqs);
            }

            for (const roleToDelete of rolesToDelete) {
                const id = roleToDelete.name.split('$')[0];
                if (idHelper.getTypeFromId(id) === 'team') {
                    const { roles: teamRoles } = await listTeamRolesWithDefaultResponse({ teamId: id });
                    const teamUsersWithRoles = await listTeamUsersWithRoles({ teamId: id });
                    if (teamRoles.length && teamRoles.find(({ name }) => roleToDelete.name === name)) {
                        await iam072021.detachTeamRole({ teamId: id, roleId: roleToDelete.name }).catch;
                    }

                    for (const teamUserWithRole of teamUsersWithRoles) {
                        if (teamUserWithRole?.roles?.length && teamUserWithRole?.roles.find(({ name }) => roleToDelete.name === name)) {
                            await iam072021.detachUserRole({ userId: teamUserWithRole.id, roleId: roleToDelete.name }).catch;
                        }
                    }

                    await iam102019.deleteRole({ roleId: roleToDelete.name }).catch;
                }

                if (idHelper.getTypeFromId(id) === 'user') {
                    const { roles } = await iam102019.listUserRoles({ userId: id }).catch(async error => {
                        if (error.status === 404) {
                            await iam102019.deleteRole({ roleId: roleToDelete.name }).catch
                        }
                        return error;
                    });
                    if (roles?.length && roles.find((role: Role) => roleToDelete.name === role.name)) {
                        await iam102019.detachUserRole({ userId: id, roleId: roleToDelete.name }).catch;
                        await iam102019.deleteRole({ roleId: roleToDelete.name }).catch;
                    } else {
                        await iam102019.deleteRole({ roleId: roleToDelete.name }).catch;
                    }
                }
            }

            for (const policyToDelete of policiesToDelete) {
                await iam102019.deletePolicy({ policyId: policyToDelete.name }).catch
            }

            resolve();
        } catch (error) {
            reject(error);
        }
    })
}

export function updatePermissions(): Promise<void> {
    return new Promise(async (resolve, reject) => {
        try {
            const [remoteRoles, remotePolicies] = await Promise.all([listRemoteRoles(), listRemotePolicies()]);
            const { account } = getDecodedToken();

            const teams = await listTeams() as unknown as Team[]; // FIX ME Fix SDK Iam
            const users = await listUsers() as unknown as User[]; // FIX ME Fix SDK Iam

            const { policiesToCreate, policiesToUpdate, policiesToDelete, rolesToDelete, rolesToCreate, rolesToUpdate } = computeUpdatePermissionsTasks({ remotePolicies, remoteRoles }, { account, teams, users });

            await executeUpdatePermissions({ policiesToCreate, policiesToUpdate, policiesToDelete, rolesToDelete, rolesToCreate, rolesToUpdate });

            resolve();
        } catch (error) {
            reject(error);
        }
    })
}

function buildRoleUpdateTasks(remoteRole: Role, localRole: Role) {
    const remoteRolePoliciesName = remoteRole.policies.map(policy => policy.name);
    const localRolePoliciesName = localRole.policies.map(policy => policy.name);

    const policiesToAttach = localRole.policies.filter(policy => remoteRolePoliciesName.indexOf(policy.name) === -1);
    const policiesToDetach = remoteRole.policies.filter(policy => localRolePoliciesName.indexOf(policy.name) === -1);

    return { policiesToAttach, policiesToDetach };

};

function mergeRolePolicies(roles: Role[]) {
    return roles.map(role => role.policies)
        .reduce((it, policies) => {
            const policyNames = it.map(p => p.name);
            const toAdd: Policy[] = [];
            policies.map(policy => {
                if (policyNames.indexOf(policy.name) === -1) {
                    toAdd.push(policy);
                }
            });
            return [...toAdd, ...it];
        }, []).sort((a, b) => a.description.localeCompare(b.description));
}

function buildLocalRolesAndPolicies({ account, teams, users }: { account: string, teams: Team[], users: User[] }) {
    let localRoles: Role[] = [];
    let localPolicies = [];

    teams.forEach(team => {
        localRoles.push(new TeamRole(roles.teamBasicAccess, { account, teamId: team.id }));
    });

    users.forEach(user => {
        localRoles.push(new UserRole(roles.fullBasicAccess, { account, userId: user.id }));
    });

    localPolicies = mergeRolePolicies(localRoles);

    return { localRoles, localPolicies };
};

function computeDeltaRoles(remoteRoles: Role[], localRoles: Role[]) {
    const rolesToCreate: Role[] = [];
    const rolesToUpdate: { role: Role, policiesToAttach: Policy[], policiesToDetach: Policy[] }[] = [];
    const rolesToDelete: Role[] = [];

    const localRoleNames = localRoles.map(localRole => localRole.name);
    remoteRoles.forEach(remoteRole => {
        const remoteRoleShouldBeDeleted = localRoleNames.indexOf(remoteRole.name) === -1;
        if (remoteRoleShouldBeDeleted) {
            rolesToDelete.push(remoteRole);
            if (remoteRole.policies.length) {
                rolesToUpdate.push({ role: remoteRole, policiesToAttach: [], policiesToDetach: remoteRole.policies });
            }
        }
    });

    localRoles.forEach(localRole => {
        const remoteRoleExisting = remoteRoles.find(remoteRole => remoteRole.name === localRole.name);
        if (remoteRoleExisting) {
            const { policiesToAttach, policiesToDetach } = buildRoleUpdateTasks(remoteRoleExisting, localRole);
            if (policiesToAttach?.length || policiesToDetach?.length) {
                rolesToUpdate.push({ role: localRole, policiesToAttach, policiesToDetach });
            }
        } else {
            rolesToCreate.push(localRole);
            rolesToUpdate.push({ role: localRole, policiesToAttach: localRole.policies, policiesToDetach: [] });
        }
    });

    return {
        rolesToCreate,
        rolesToUpdate,
        rolesToDelete,
    };
};

function sortKeys(x: any): any {
    if (Array.isArray(x) && typeof x[0] === 'object') { // re-order array of object
        if (x[0].description) {
            const nested = x.sort((a, b) => a.description.localeCompare(b.description));
            return nested.map(sortKeys);
        }
        if (x[0].effect) {
            const nested = x.sort((a, b) => a.effect.localeCompare(b.effect));
            return nested.map(sortKeys);
        }
    }
    if (Array.isArray(x)) {
        return x.sort();
    }
    if (typeof x !== 'object' || !x) {
        return x;
    }

    return Object.keys(x).sort().reduce((o, k) => ({ ...o, [k]: sortKeys(x[k]) }), {});
};

function policyNeedUpdate(remotePolicy: Policy, localPolicy: Policy) {
    if (remotePolicy.statement && remotePolicy.statement.length !== localPolicy.statement.length) {
        return true;
    }

    const remote = sortKeys(JSON.parse(JSON.stringify(remotePolicy)));
    const local = sortKeys(JSON.parse(JSON.stringify(localPolicy)));
    if (JSON.stringify(remote) !== JSON.stringify(local)) {
        return true;
    }

    return false;
};


function computeDeltaPolicies(remotePolicies: Policy[], localPolicies: Policy[]) {
    const policiesToUpdate: Policy[] = [];
    const policiesToCreate: Policy[] = [];
    const policiesToDelete: Policy[] = [];
    const remotePolicyNames = remotePolicies.map(remotePolicy => remotePolicy.name);
    const localPolicyNames = localPolicies.map(localPolicy => localPolicy.name);

    remotePolicies.forEach(remotePolicy => {
        const isOldPolicy = localPolicyNames.indexOf(remotePolicy.name) === -1;
        if (isOldPolicy) {
            policiesToDelete.push(remotePolicy);
        }
    });

    localPolicies.forEach(localPolicy => {
        const remotePolicy = remotePolicies.find(remotePolicy => remotePolicy.name === localPolicy.name);
        const isNewPolicy = remotePolicyNames.indexOf(localPolicy.name) === -1;
        if (isNewPolicy) {
            policiesToCreate.push(localPolicy);
        } else if (remotePolicy) {
            if (policyNeedUpdate(remotePolicy, localPolicy)) {
                policiesToUpdate.push(localPolicy);
            }
        }
    });

    return {
        policiesToUpdate,
        policiesToCreate,
        policiesToDelete
    };
};

export function computeUpdatePermissionsTasks({ remotePolicies, remoteRoles }: { remotePolicies: IPolicy[], remoteRoles: HydratedRole[] }, { account, teams = [], users = [] }: { account: string, teams?: Team[], users?: User[] }) {
    const { localRoles, localPolicies } = buildLocalRolesAndPolicies({ account, teams, users });
    const { rolesToCreate, rolesToUpdate, rolesToDelete } = computeDeltaRoles(remoteRoles as unknown as Role[], localRoles);
    const { policiesToUpdate, policiesToCreate, policiesToDelete } = computeDeltaPolicies(remotePolicies as unknown as Policy[], localPolicies);

    return {
        policiesToCreate,
        policiesToUpdate,
        policiesToDelete,
        rolesToDelete,
        rolesToCreate,
        rolesToUpdate,
    };
};

const permissionsService = {
    updatePermissions,
}

export default permissionsService;